// Closing gaps between a prefab's pieces: a head that hovers over its shoulders, a skirt that
// stops short of the waist, an arm with a voxel of air at the shoulder.
//
// The voxels of every prefab are grouped into 26-connected pieces. A piece (other than a
// stray single voxel, which is an eye or a spark) separated from the main body by a gap of
// one to three voxels gets a connector of voxels in that gap: a 2 x 2 column for a vertical
// gap (a neck), a thin line for a sideways one. Connectors lie strictly between voxels that
// already exist, so a unit's bounding box never grows. Deliberate floaters (halos, wisps,
// the wraith's trailing ghost) are skipped.
import type { BlockVoxel, CharacterPart } from './types.js';

type P = { x: number; y: number; z: number };
const k = (v: P) => `${v.x},${v.y},${v.z}`;

/** Pieces that are meant to hover. */
const FLOATING_PART = /halo|wisp|aura|ghost|spark|orbit|flame/i;
const GHOSTS = new Set(['wraith', 'banshee']);
const MAX_GAP = 3;

function pieces(voxels: readonly P[]): P[][] {
  const set = new Set(voxels.map(k));
  const seen = new Set<string>();
  const out: P[][] = [];
  for (const v of voxels) {
    if (seen.has(k(v))) continue;
    const comp: P[] = [];
    const stack = [v];
    seen.add(k(v));
    while (stack.length) {
      const c = stack.pop() as P;
      comp.push(c);
      for (let dx = -1; dx <= 1; dx++)
        for (let dy = -1; dy <= 1; dy++)
          for (let dz = -1; dz <= 1; dz++) {
            if (!dx && !dy && !dz) continue;
            const n = { x: c.x + dx, y: c.y + dy, z: c.z + dz };
            if (set.has(k(n)) && !seen.has(k(n))) {
              seen.add(k(n));
              stack.push(n);
            }
          }
    }
    out.push(comp);
  }
  return out.sort((a, b) => b.length - a.length);
}

const cheb = (a: P, b: P) =>
  Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y), Math.abs(a.z - b.z));

/** The closest pair (one voxel from each piece) by Chebyshev distance. */
function closest(a: readonly P[], b: readonly P[]): { from: P; to: P; d: number } {
  let best = { from: a[0] as P, to: b[0] as P, d: Number.POSITIVE_INFINITY };
  for (const p of a) {
    for (const q of b) {
      const d = cheb(p, q);
      if (d < best.d) best = { from: p, to: q, d };
    }
  }
  return best;
}

/** The connector voxels between `from` and `to`, exclusive of both. */
function connector(from: P, to: P, side: readonly P[], main: readonly P[]): P[] {
  const gap = cheb(from, to) - 1;
  const out: P[] = [];
  const vertical =
    from.x === to.x && from.z === to.z
      ? true
      : Math.abs(from.y - to.y) >= Math.max(Math.abs(from.x - to.x), Math.abs(from.z - to.z));
  for (let i = 1; i <= gap; i++) {
    const t = i / (gap + 1);
    const c = {
      x: Math.round(from.x + (to.x - from.x) * t),
      y: Math.round(from.y + (to.y - from.y) * t),
      z: Math.round(from.z + (to.z - from.z) * t),
    };
    out.push(c);
    if (vertical) {
      // A neck is a column, not a thread: widen to the cells the two ends share.
      for (const [dx, dz] of [
        [1, 0],
        [0, 1],
        [1, 1],
        [-1, 0],
        [0, -1],
      ]) {
        const w = { x: c.x + (dx as number), y: c.y, z: c.z + (dz as number) };
        const nearSide = side.some((s) => s.x === w.x && s.z === w.z);
        const nearMain = main.some((m) => m.x === w.x && m.z === w.z);
        if (nearSide && nearMain) out.push(w);
      }
    }
  }
  return out;
}

/** The parts with connectors added to the nearest part of the piece they attach. */
export function bridgeGaps(id: string, parts: readonly CharacterPart[]): CharacterPart[] {
  const all = parts.flatMap((p) => p.voxels);
  const comps = pieces(all);
  if (comps.length < 2) return [...parts];
  const main = comps[0] as P[];
  const added = new Map<string, BlockVoxel[]>();
  const taken = new Set(all.map(k));
  for (const comp of comps.slice(1)) {
    if (comp.length < 4) continue; // an eye or a spark, not a limb
    const owner = parts.find((p) =>
      p.voxels.some((v) => comp.some((c) => c.x === v.x && c.y === v.y && c.z === v.z)),
    );
    if (!owner || FLOATING_PART.test(owner.name)) continue;
    if (GHOSTS.has(id) && /leg|tail|wisp/i.test(owner.name)) continue;
    const { from, to, d } = closest(comp, main);
    if (d < 2 || d - 1 > MAX_GAP) continue;
    const end = owner.voxels.find(
      (v) => v.x === from.x && v.y === from.y && v.z === from.z,
    ) as BlockVoxel;
    for (const c of connector(from, to, comp, main)) {
      if (taken.has(k(c))) continue;
      taken.add(k(c));
      const list = added.get(owner.name) ?? [];
      list.push({
        x: c.x,
        y: c.y,
        z: c.z,
        color: end.color,
        roughness: end.roughness,
        metalness: end.metalness,
      });
      added.set(owner.name, list);
    }
  }
  return parts.map((p) =>
    added.has(p.name) ? { ...p, voxels: [...p.voxels, ...(added.get(p.name) as BlockVoxel[])] } : p,
  );
}
